import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Gamepad2, BookOpen, Video, Trophy, User, MessageSquare, Settings, LogOut, X, Sparkles, Flame, TrendingUp, ChevronDown, Award } from 'lucide-react';
import { useAuth } from '../App';
import ProactiveCoach from '../components/ProactiveCoach';

const AppShell = ({ children, gameStateFromPlay = null }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [coachOpen, setCoachOpen] = useState(false);
  const [userProgress, setUserProgress] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [gameState, setGameState] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Use game state from Play page if provided, otherwise fetch
  useEffect(() => {
    if (gameStateFromPlay) {
      console.log('[APPSHELL] Using game state from Play.js:', gameStateFromPlay);
      setGameState(gameStateFromPlay);
    } else if (coachOpen) {
      fetchGameState();
      const interval = setInterval(fetchGameState, 2000);
      return () => clearInterval(interval);
    }
  }, [coachOpen, gameStateFromPlay]);

  const fetchGameState = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform/game/state`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        console.log('[APPSHELL] Game state fetched:', data);
        setGameState(data.game); // FIX: API returns {game: ...} not {state: ...}
      }
    } catch (error) {
      console.error('Failed to fetch game state:', error);
    }
  };

  // Load user progress for header stats
  useEffect(() => {
    loadUserProgress();
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('[data-testid="user-menu-btn"]') && !event.target.closest('[data-testid="user-dropdown"]')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const loadUserProgress = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform/learning/progress`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUserProgress(data);
      }
    } catch (error) {
      console.error('Failed to load user progress:', error);
    }
  };

  const modules = [
    { id: 'play', label: 'PLAY', icon: Gamepad2, path: '/app/play' },
    { id: 'learn', label: 'LEARN', icon: BookOpen, path: '/app/learn' },
    { id: 'watch', label: 'WATCH', icon: Video, path: '/app/watch' },
    { id: 'compete', label: 'COMPETE', icon: Trophy, path: '/app/compete' },
    { id: 'profile', label: 'PROFILE', icon: User, path: '/app/profile' },
  ];

  const isActive = (path) => location.pathname.startsWith(path);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#0B0F14] text-foreground">
      {/* Enhanced Top Navigation Bar */}
      <nav className="sticky top-0 z-50 border-b border-[#C9A227]/20 bg-[#0B0F14]/95 backdrop-blur-xl shadow-lg shadow-black/20">
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#C9A227] to-[#E1C25B] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#0B0F14]" />
              </div>
              <span className="text-xl font-heading font-bold text-foreground">
                LUCK <span className="text-[#C9A227]">or</span> SKILLS
              </span>
            </Link>

            {/* Module Navigation */}
            <div className="flex items-center gap-2">
              {modules.map((module) => {
                const Icon = module.icon;
                const active = isActive(module.path);
                return (
                  <Link
                    key={module.id}
                    to={module.path}
                    data-testid={`nav-${module.id}`}
                    className={`
                      flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all font-heading text-sm font-semibold
                      ${active 
                        ? 'bg-[#C9A227] text-[#0B0F14] shadow-lg shadow-[#C9A227]/20' 
                        : 'text-foreground/70 hover:text-foreground hover:bg-white/5'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    {module.label}
                  </Link>
                );
              })}
            </div>

            {/* Right Side: Stats + Actions */}
            <div className="flex items-center gap-4">
              {/* User Stats Quick View */}
              {userProgress && (
                <div className="flex items-center gap-4 px-4 py-2 bg-[#121923] border border-white/10 rounded-lg">
                  {/* Level */}
                  <div className="flex items-center gap-2" data-testid="header-level">
                    <Award className="w-4 h-4 text-[#C9A227]" />
                    <div className="text-xs">
                      <div className="text-foreground/60">Level</div>
                      <div className="font-bold text-[#C9A227]">{userProgress.level}</div>
                    </div>
                  </div>

                  {/* XP Progress Bar */}
                  <div className="flex items-center gap-2" data-testid="header-xp">
                    <TrendingUp className="w-4 h-4 text-blue-400" />
                    <div className="text-xs">
                      <div className="text-foreground/60">XP</div>
                      <div className="font-bold text-foreground">{userProgress.xp}</div>
                    </div>
                  </div>

                  {/* Streak */}
                  {userProgress.current_streak > 0 && (
                    <div className="flex items-center gap-2" data-testid="header-streak">
                      <Flame className="w-4 h-4 text-orange-500" />
                      <div className="text-xs">
                        <div className="text-foreground/60">Streak</div>
                        <div className="font-bold text-orange-500">{userProgress.current_streak}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* AI Coach - Enhanced Button */}
              <button
                onClick={() => setCoachOpen(!coachOpen)}
                data-testid="open-coach-btn"
                className="relative flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#C9A227] to-[#E1C25B] text-[#0B0F14] rounded-lg hover:shadow-lg hover:shadow-[#C9A227]/30 transition-all font-heading font-bold text-sm group"
              >
                <MessageSquare className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span>Ask the Pro</span>
                <Sparkles className="w-3 h-3 opacity-70" />
              </button>

              {/* User Menu Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  data-testid="user-menu-btn"
                  className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-lg transition-colors"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-[#C9A227]/30" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#C9A227]" />
                    </div>
                  )}
                  <ChevronDown className={`w-4 h-4 text-foreground/60 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-[#121923] border border-[#C9A227]/20 rounded-xl shadow-2xl overflow-hidden" data-testid="user-dropdown">
                    {/* User Info */}
                    <div className="p-4 bg-gradient-to-br from-[#0F3227] to-[#071A14] border-b border-white/10">
                      <div className="flex items-center gap-3 mb-2">
                        {user?.picture ? (
                          <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full border-2 border-[#C9A227]" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                            <User className="w-6 h-6 text-[#C9A227]" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="font-heading font-bold text-foreground">{user?.name}</div>
                          <div className="text-xs text-foreground/60">{user?.email}</div>
                        </div>
                      </div>
                      
                      {/* Membership Badge */}
                      <div className="mt-3">
                        <span className={`
                          inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold
                          ${user?.membership_status === 'premium' 
                            ? 'bg-[#C9A227] text-[#0B0F14]' 
                            : 'bg-white/10 text-foreground/60'
                          }
                        `}>
                          {user?.membership_status === 'premium' ? '👑 Premium' : '🎯 Free'}
                        </span>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    {userProgress && (
                      <div className="p-4 border-b border-white/10">
                        <div className="text-xs text-foreground/60 mb-2 font-semibold">Your Progress</div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-[#0F1419] rounded-lg p-2">
                            <div className="text-lg font-bold text-[#C9A227]">{userProgress.level}</div>
                            <div className="text-xs text-foreground/60">Level</div>
                          </div>
                          <div className="bg-[#0F1419] rounded-lg p-2">
                            <div className="text-lg font-bold text-blue-400">{userProgress.xp}</div>
                            <div className="text-xs text-foreground/60">XP</div>
                          </div>
                          <div className="bg-[#0F1419] rounded-lg p-2">
                            <div className="text-lg font-bold text-orange-500">{userProgress.current_streak}</div>
                            <div className="text-xs text-foreground/60">Streak</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="p-2">
                      <Link
                        to="/app/profile"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 rounded-lg transition-colors w-full"
                        data-testid="header-profile-link"
                      >
                        <User className="w-4 h-4 text-[#C9A227]" />
                        <span className="text-sm font-medium">View Profile</span>
                      </Link>

                      <Link
                        to="/app/learn"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 rounded-lg transition-colors w-full"
                        data-testid="header-learn-link"
                      >
                        <BookOpen className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium">Continue Learning</span>
                      </Link>

                      <button
                        onClick={() => {
                          setCoachOpen(true);
                          setShowUserMenu(false);
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 rounded-lg transition-colors w-full text-left"
                        data-testid="header-coach-link"
                      >
                        <MessageSquare className="w-4 h-4 text-green-400" />
                        <span className="text-sm font-medium">Ask AI Coach</span>
                      </button>

                      {user?.role === 'admin' && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 rounded-lg transition-colors w-full"
                          data-testid="header-admin-link"
                        >
                          <Settings className="w-4 h-4 text-purple-400" />
                          <span className="text-sm font-medium">Admin Panel</span>
                        </Link>
                      )}

                      <div className="my-2 border-t border-white/10"></div>

                      <button
                        onClick={() => {
                          handleLogout();
                          setShowUserMenu(false);
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-red-600/10 hover:text-red-400 rounded-lg transition-colors w-full text-left"
                        data-testid="header-logout-btn"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm font-medium">Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="min-h-[calc(100vh-3.5rem)]">
        <Outlet />
      </main>

      {/* Proactive AI Coach - Right Side Panel */}
      <ProactiveCoach 
        open={coachOpen} 
        onClose={() => setCoachOpen(false)}
        gameState={gameState}
      />
    </div>
  );
};

export default AppShell;